import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axiosInstance from '../../../axiosInstance';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Typography from 'apollo-react/components/Typography';
import DataTableComponent from '../../common/DataTable/PrimeDataTable';
import RadioInput from '../../common/Form/radioInput';
import { getCalendarDaysUntilLicenseEnd } from '../../../utils/hcpLicenseRenewalDays';

/** Prime row class: red ≤30, orange ≤60, yellow ≤90 (expired counts as red). */
function expiryHighlightClass(row) {
  const days = getCalendarDaysUntilLicenseEnd(row?.license_end_date);
  if (days == null) return '';
  if (days <= 30) return 'hcp-registry-expiry-row--30';
  if (days <= 60) return 'hcp-registry-expiry-row--60';
  if (days <= 90) return 'hcp-registry-expiry-row--90';
  return '';
}

const HcpLicenseListTable = ({ persona }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [paginationParams, setPaginationParams] = useState({
    limit: 10,
    offset: 0,
    sortField: 'createdAt',
    sortOrder: -1,
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchHcpLicenseList();
  }, [paginationParams]);

  useEffect(() => {
    setPaginationParams((prev) => ({ ...prev, offset: 0 }));
  }, [expiryFilter]);

  const fetchHcpLicenseList = async () => {
    setLoading(true);
    try {
      const endpoint =
        persona === 'enduser'
          ? `/api/hcp-licenses-list/enduser`
          : `/api/hcp-licenses-list`;

      const response = await axiosInstance.get(endpoint, {
        withCredentials: true,
      });
      setData(response?.data || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (expiryFilter === 'all') return data;
    if (expiryFilter === 'expired') {
      return data.filter((row) => {
        const days = getCalendarDaysUntilLicenseEnd(row?.license_end_date);
        return days != null && days < 0;
      });
    }
    const maxDays =
      expiryFilter === '90'
        ? 90
        : expiryFilter === '60'
          ? 60
          : expiryFilter === '30'
            ? 30
            : null;
    if (maxDays == null) return data;
    return data.filter((row) => {
      const days = getCalendarDaysUntilLicenseEnd(row?.license_end_date);
      if (days == null) return false;
      return days >= 0 && days <= maxDays;
    });
  }, [data, expiryFilter]);

  const rowClassName = useCallback((row) => expiryHighlightClass(row), []);

  const onDataTableEvent = (event) => {
    setPaginationParams((prev) => ({
      ...prev,
      limit: event.rows,
      offset: event.first,
      sortField: event.sortField || prev.sortField,
      sortOrder: event.sortOrder || prev.sortOrder,
    }));
  };

  const columns = [
    {
      field: 'hcp_emirates_id',
      header: 'Emirates ID',
      sortable: true,
      filter: true,
    },
    {
      field: 'hcp_name',
      header: 'Name',
      sortable: true,
      filter: true,
    },
    {
      field: 'license_number',
      header: 'License #',
      sortable: true,
      filter: true,
    },
    {
      field: 'requestProgressStatus',
      header: 'Status',
      sortable: true,
      filter: true,
      body: (rowData) => rowData?.requestProgressStatus || 'Active',
    },
    {
      field: 'license_issue_date',
      header: t('hcpRegistry.licenseIssueDateColumn'),
      sortable: true,
      filter: true,
      body: (rowData) => {
        const v = rowData?.license_issue_date;
        if (!v) return '-';
        const d = new Date(v);
        return Number.isNaN(d.getTime())
          ? String(v)
          : d.toLocaleDateString('en-GB');
      },
    },
    {
      field: 'license_end_date',
      header: t('hcpRegistry.licenseRenewalDateColumn'),
      sortable: true,
      filter: true,
      body: (rowData) => {
        const v = rowData?.license_end_date;
        if (!v) return '-';
        const d = new Date(v);
        return Number.isNaN(d.getTime())
          ? String(v)
          : d.toLocaleDateString('en-GB');
      },
    },
    {
      field: 'registry_update_source',
      header: t('hcpRegistry.lastUpdateSource'),
      sortable: true,
      filter: true,
      body: (rowData) => {
        const src = rowData?.registry_update_source;
        if (src === 'sheet_import') {
          return t('hcpRegistry.sourceSheetImport');
        }
        return t('hcpRegistry.sourceRequest');
      },
    },
    {
      field: 'updatedAt',
      header: 'Updated At',
      sortable: true,
      filter: true,
      body: (rowData) => {
        return rowData.updatedAt
          ? new Date(rowData.updatedAt).toLocaleString('en-GB')
          : '-';
      },
    },
  ];

  const actions = [
    {
      text: 'View Details',
      color: 'primary',
      onClick: (row) =>
        navigate(`/${persona}/hcp-licenses-list/details/${row.id}`),
    },
  ];

  return (
    <div>
      <style>{`
        .hcp-registry-expiry-row--90 > td { background-color: rgba(255, 249, 196, 0.225) !important; }
        .hcp-registry-expiry-row--60 > td { background-color: rgba(255, 224, 178, 0.225) !important; }
        .hcp-registry-expiry-row--30 > td { background-color: rgba(255, 205, 210, 0.225) !important; }
      `}</style>
      <div style={{ marginBottom: '20px' }}>
        <Typography
          variant="bodyDefault"
          emphasis="high"
          style={{ marginBottom: '10px', display: 'block' }}
        >
          {t('hcpRegistry.expiryFilterLabel')}
        </Typography>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '20px',
            alignItems: 'center',
          }}
        >
          <RadioInput
            groupName="hcpRegistryExpiryFilter"
            checkedValue={expiryFilter}
            label={t('hcpRegistry.expiryFilterAll')}
            value="all"
            onChange={setExpiryFilter}
          />
          <RadioInput
            groupName="hcpRegistryExpiryFilter"
            checkedValue={expiryFilter}
            label={t('hcpRegistry.expiryFilter90')}
            value="90"
            onChange={setExpiryFilter}
          />
          <RadioInput
            groupName="hcpRegistryExpiryFilter"
            checkedValue={expiryFilter}
            label={t('hcpRegistry.expiryFilter60')}
            value="60"
            onChange={setExpiryFilter}
          />
          <RadioInput
            groupName="hcpRegistryExpiryFilter"
            checkedValue={expiryFilter}
            label={t('hcpRegistry.expiryFilter30')}
            value="30"
            onChange={setExpiryFilter}
          />
          <RadioInput
            groupName="hcpRegistryExpiryFilter"
            checkedValue={expiryFilter}
            label={t('hcpRegistry.expiryFilterExpired')}
            value="expired"
            onChange={setExpiryFilter}
          />
        </div>
      </div>
      <DataTableComponent
        data={filteredData}
        columns={columns}
        loading={loading}
        paginationParams={paginationParams}
        totalRecords={filteredData?.length ?? 0}
        onDataTableEvent={onDataTableEvent}
        enableCreate={false}
        enablePagination={true}
        enableLazyLoading={false}
        enableExport={true}
        actions={actions}
        rowClassName={rowClassName}
        sectionName={`HCP-Licenses-List-${new Date().getDate()}-${
          new Date().getMonth() + 1
        }-${new Date().getFullYear()}-${new Date().getHours()}-${new Date().getMinutes()}`}
      />
    </div>
  );
};

export default HcpLicenseListTable;
